import { createStyles, makeStyles, Theme } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import React from "react";
import {
  BOX_SHADOWS,
  DATA_TYPES,
  FONTS,
  PRIMARY_COLOR,
  SECONDARY_COLOR,
} from "../../constants";
import { formatNumber } from "../../helpers/FormatNumber";
import { stateType } from "../../state";
import { trimString } from "../Common/helpers";
import { HelpIcon, helpIconType } from "../Common/ToolTipIcon";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      boxShadow: BOX_SHADOWS,
      backgroundColor: "white",
      height: "500px",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      fontFamily: FONTS,
      borderRadius: "10px",
      alignItems: "center",
    },
    root: {
      margin: "20px",
      width: "95%",

      borderColor: "black",
    },
    paper: {
      width: "100%",
      marginBottom: theme.spacing(1),
    },
    table: { whiteSpace: "nowrap" },
    visuallyHidden: {
      border: 0,
      clip: "rect(0 0 0 0)",
      height: 1,
      margin: -1,
      overflow: "hidden",
      padding: 0,
      position: "absolute",
      top: 20,
      width: 1,
    },
    tableStyle: {
      color: PRIMARY_COLOR,
      borderWidth: 0,
      borderColor: "grey",
      borderStyle: "solid",
      whiteSpace: "nowrap",
      paddingTop: "5px",
      paddingBottom: "5px",
      paddingRight: "10px",
      width: "70px",
      maxWidth: "70px",
      fontSize: "13px",
      fontFamily: FONTS,
    },
    headerCell: {
      color: PRIMARY_COLOR,
      borderWidth: 0,
      borderColor: "grey",
      borderStyle: "solid",
      whiteSpace: "nowrap",
      paddingTop: "5px",
      paddingBottom: "5px",
      paddingRight: "10px",
      display: "flex",
      flexDirection: "row",
    },
    header: {
      color: PRIMARY_COLOR,
      borderWidth: 0,
      borderColor: "grey",
      borderStyle: "solid",
      display: "flex",
      flexDirection: "row",
    },
    tooltip: { backgroundColor: SECONDARY_COLOR, fontSize: "12px" },
    tooltipArrow: { color: PRIMARY_COLOR },
  })
);

interface Data {
  colName: string;
  colType: string;
  avg: string;
  median: string;
  min: string;
  max: string;
  unq: string;
}

function createData(
  colName: string,
  colType: string,
  avg: string,
  median: string,
  min: string,
  max: string,
  unq: string
): Data {
  return { colName, colType, avg, median, min, max, unq };
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: HeadCell[] = [
  {
    id: "colName",
    numeric: false,
    disablePadding: true,
    label: "Column Name",
  },
  { id: "colType", numeric: true, disablePadding: false, label: "Type" },

  {
    id: "min",
    numeric: true,
    disablePadding: false,
    label: "Min/Least Common",
  },
  {
    id: "max",
    numeric: true,
    disablePadding: false,
    label: "Max/Most Common",
  },
  {
    id: "unq",
    numeric: true,
    disablePadding: false,
    label: "Unique Values",
  },
  { id: "avg", numeric: true, disablePadding: false, label: "Average" },
  { id: "median", numeric: true, disablePadding: false, label: "Median" },
];

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  numSelected: number;
  state: stateType;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    classes,
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  const qColumns = [
    "Type",
    "Min/Least Common",
    "Max/Most Common",
    "Unique Values",
  ];

  let tmp: Map<string, helpIconType> = new Map();
  tmp.set("Type", {
    title:
      "The column type as viewed by our ML models. Numeric: a number, like the price of a house. Categorical: categories, like a cat/dog.",
    marginLeft: "37px",
    paddingTop: "8px",
  });
  tmp.set("Min/Least Common", {
    title:
      "The least common value if the data is Categorical, and the minumum value if it is Numeric.",
    marginLeft: "140px",
    paddingTop: "8px",
  });
  tmp.set("Max/Most Common", {
    title:
      "The most common value if the data is Categorical, and the maximum value if it is Numeric.",
    marginLeft: "143px",
    paddingTop: "8px",
  });
  tmp.set("Unique Values", {
    title: "The number of distinct values in this column.",
    marginLeft: "105px",
    paddingTop: "8px",
  });

  return (
    <TableHead
      style={{
        backgroundColor: "rgb(240,240,240)",
      }}
    >
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            sortDirection={orderBy === headCell.id ? order : false}
            style={{
              fontSize: "13px",
              fontFamily: FONTS,
              fontWeight: "bold",
            }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </span>
              ) : null}
              {qColumns.includes(headCell.label) ? (
                <HelpIcon
                  title={tmp.get(headCell.label).title}
                  marginLeft={tmp.get(headCell.label).marginLeft}
                  paddingTop="8px"
                ></HelpIcon>
              ) : (
                <div></div>
              )}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable({ state }: stateType) {
  const classes = useStyles();
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("colName");
  const [selected, setSelected] = React.useState<string[]>([]);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(true);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleClick = (event: React.MouseEvent<unknown>, name: string) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const makeData = () => {
    let data: Data[] = [];

    for (let col of state.saveState.dataset.columns) {
      let nValues = Object.entries(col.rawFrequency).length;
      let mostCommon =
        col.columnType == DATA_TYPES.number
          ? formatNumber(col.colStats.max)
          : col.colStats.mostCommon;

      let leastCommon =
        col.columnType == DATA_TYPES.number
          ? formatNumber(col.colStats.min)
          : col.colStats.leastCommon;

      let colType = col.columnType; // === "Number" ? "Numeric" : "Categorical";
      if (colType == DATA_TYPES.unusable) {
        continue;
      }
      data.push(
        createData(
          col.columnName,
          colType,

          leastCommon,
          mostCommon,
          formatNumber(nValues),
          formatNumber(col.colStats.mean),
          formatNumber(col.colStats.median)
        )
      );
    }
    return data;
  };

  let rows = makeData();

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  return (
    <div className={classes.root}>
      <Paper className={classes.paper} elevation={0}>
        <TableContainer style={{ width: "100%", height: "420px" }}>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={"small"}
            aria-label="enhanced table"
            style={{ height: "420px" }}
          >
            <EnhancedTableHead
              state={{ state }}
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={() => {}}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {stableSort(rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      hover
                      onClick={(event) =>
                        handleClick(event, row.colName as string)
                      }
                    >
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        className={classes.tableStyle}
                      >
                        {trimString(row.colName as string, 20)}
                      </TableCell>
                      <TableCell align="right" className={classes.tableStyle}>
                        {row.colType}
                      </TableCell>
                      <TableCell align="right" className={classes.tableStyle}>
                        {trimString(row.avg as string, 20)}
                      </TableCell>
                      <TableCell align="right" className={classes.tableStyle}>
                        {trimString(row.median as string, 20)}
                      </TableCell>
                      <TableCell align="right" className={classes.tableStyle}>
                        {trimString(row.min as string, 20)}
                      </TableCell>
                      <TableCell align="right" className={classes.tableStyle}>
                        {trimString(row.max as string, 20)}
                      </TableCell>
                      <TableCell align="right" className={classes.tableStyle}>
                        {trimString(row.unq as string, 20)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
        />
      </Paper>
    </div>
  );
}

export function TableBox({ state }: stateType) {
  let classes = useStyles();
  return (
    <div className={classes.container}>
      <EnhancedTable state={state}></EnhancedTable>
    </div>
  );
}
